import { Link } from 'react-router-dom'

const guide = {
  slug: 'lock-screen-time-so-it-cant-be-bypassed',
  category: 'holds',
  kicker: 'Screen Time · Lockdown',
  title: 'How to lock Screen Time so it can\'t be bypassed — and the one hole Apple leaves open',
  seoTitle: 'How to Lock Screen Time So It Can\'t Be Bypassed',
  description: `Block at End of Limit, a separate passcode, no account changes, no app installs: every Screen Time setting that stops a bypass, and the one hole Apple leaves open.`,
  published: '2026-09-03',
  minutes: 8,
  intro: `Screen Time is the tool everyone reaches for first, and everyone
          finds the way around it by the end of the week. Most of those ways
          have a setting that closes them, and this guide goes through them
          all, in the order they get discovered. It also says plainly which
          hole no setting closes, because that's the one that decides whether
          you're locking a device or leaving yourself a note.`,
  next: ['is-screen-time-enough-to-block-porn', 'supervised-iphone-explained', 'how-to-block-porn-on-iphone'],
  body: (
    <>
      <h2>Step 1 — A Screen Time passcode that isn't your unlock code</h2>
      <p>
        <b>Settings → Screen Time → Lock Screen Time Settings</b>. Choose four
        digits you don't use anywhere else. Without this, every restriction
        below is a suggestion.
      </p>

      <h2>Step 2 — Block at End of Limit (kills "Ignore Limit")</h2>
      <p>
        App Limits show an <b>Ignore Limit</b> button when time runs out,
        which is how most limits die. In each limit, or under
        <b> Settings → Screen Time → App Limits</b>, turn on <b>Block at End of
        Limit</b>. The button is replaced by a passcode prompt.
      </p>

      <h2>Step 3 — Limit Adult Websites, and take away Private Browsing</h2>
      <p>
        <b>Content &amp; Privacy Restrictions → Content Restrictions → Web
        Content → Limit Adult Websites</b>. Safari's Private Browsing
        disappears while this is on. Add anything Apple's list misses under
        <b> Never Allow</b>. Full detail:
        <Link to="/guides/how-to-block-porn-on-safari"> blocking porn in Safari</Link>.
      </p>

      <h2>Step 4 — No new browsers, no deleting the blocker</h2>
      <p>
        Under <b>Content &amp; Privacy Restrictions → iTunes &amp; App Store
        Purchases</b>, set <b>Installing Apps</b> and <b>Deleting Apps</b> to
        <b> Don't Allow</b>. This is what stops "download Chrome" and "delete
        the filter app". It also stops you installing anything else until
        you enter the passcode, which is the price.
      </p>

      <h2>Step 5 — No account changes, no time-zone games</h2>
      <p>
        Under <b>Content &amp; Privacy Restrictions → Allow Changes</b>, set
        <b> Account Changes</b> to <b>Don't Allow</b>: signing out of the Apple
        ID and back in is one of the classic resets. Set <b>Passcode
        Changes</b> to Don't Allow too. Downtime schedules are tied to the
        device clock; restricting location and account changes limits the
        usual clock tricks.
      </p>

      <h2>Step 6 — Siri, Spotlight and hidden browsers</h2>
      <p>
        <b>Content Restrictions → Siri → Web Search Content → Don't Allow</b>.
        Under <b>Allowed Apps</b>, review what's on. Remember that apps with a
        built-in browser (mail, social, notes) are a browser: Limit Adult
        Websites reaches those that use Apple's web view, but not everything.
      </p>

      <h2>Step 7 — Have someone else set the passcode</h2>
      <p>
        The standard forum advice, and it does help: a friend or partner
        types the four digits, you never see them. Now you can't undo the
        settings on impulse. Two costs: you've involved someone in the most
        private fight you have, and, as the next section explains, the
        passcode is still recoverable by you.
      </p>

      <h2>The hole no setting closes</h2>
      <p>
        On the passcode screen, Apple offers <b>Forgot Passcode?</b> It
        resets the Screen Time passcode using the <b>Apple ID signed in on
        the device</b>. That Apple ID is yours. Whoever typed the four digits,
        you hold the account that overrides them. This isn't a bug; it's how
        Apple protects people locked out of their own phones. It also means
        that Screen Time, fully configured, with a passcode set by someone
        else, is still a lock whose key is in your pocket. The five ways it
        gets undone are laid out in
        <Link to="/guides/is-screen-time-enough-to-block-porn"> is Screen Time enough</Link>.
      </p>

      <h2>What closes it: supervision</h2>
      <p>
        Apple has a second mechanism, built for organisations that hand out
        iPhones and need restrictions to stick: <b>supervision</b>. A
        supervised device can carry a configuration profile that cannot be
        removed from the phone, that sets a filtering DNS resolver for every
        app, and that forbids adding a VPN. There's no passcode to recover,
        because there is no passcode: the only way off is a deliberate
        request from an account, after a delay. How it works and what it
        does to your phone: <Link to="/guides/supervised-iphone-explained">supervised iPhone explained</Link>.
      </p>
      <p>
        That's what SELFCNTRL does on iPhone and iPad. Keep Screen Time on
        underneath; it costs nothing. But the thing that holds at 2am is the
        profile you can't remove. Setup takes ten minutes, once:
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
      </p>

      <h2>Checklist</h2>
      <table>
        <thead>
          <tr><th>Bypass</th><th>Setting that closes it</th><th>Closed for good?</th></tr>
        </thead>
        <tbody>
          <tr><td>Ignore Limit</td><td>Block at End of Limit</td><td>Until you enter the passcode</td></tr>
          <tr><td>Private Browsing</td><td>Limit Adult Websites</td><td>Until you enter the passcode</td></tr>
          <tr><td>Install another browser</td><td>Installing Apps: Don't Allow</td><td>Until you enter the passcode</td></tr>
          <tr><td>Delete the blocker app</td><td>Deleting Apps: Don't Allow</td><td>Until you enter the passcode</td></tr>
          <tr><td>Sign out of Apple ID</td><td>Account Changes: Don't Allow</td><td>Until you enter the passcode</td></tr>
          <tr><td>Forgot Passcode? → Apple ID reset</td><td>None</td><td>No — only supervision removes the passcode from the equation</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'How do I stop myself from bypassing Screen Time?',
      a: `Turn on Block at End of Limit, Limit Adult Websites, Installing
          Apps and Account Changes: Don't Allow, and use a separate passcode.
          Then accept that the Forgot Passcode option resets it with your
          Apple ID. To take yourself out of the equation entirely, the
          device has to be supervised.`
    },
    {
      q: 'Can I lock Screen Time without a passcode someone else knows?',
      a: `You can set one you don't memorise (type it without looking, or
          let a random generator pick it), but the Apple ID reset is still
          available to you. A supervised profile is the only Screen
          Time-style restriction that has no passcode to recover.`
    },
    {
      q: 'Does Block at End of Limit work for websites?',
      a: `App Limits can include specific websites, and Block at End of
          Limit applies to them. For adult content as a category, Limit
          Adult Websites is the setting, and it isn't time-based.`
    },
    {
      q: 'Is supervising my own iPhone allowed?',
      a: `Yes. Supervision is an Apple feature of iOS; it needs a Mac or a
          Windows PC once, and it can involve resetting the device, so you
          back up first and restore after. SELFCNTRL's supervisor walks you
          through it in about ten minutes.`
    }
  ]
}

export default guide
